import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { getSessionBranchId } from "../utils/sessionBranch";
import { postMetalTransfer, isErpApiSuccess } from "../erpClient";
import { searchProductGroupSelectOptions } from "../utils/productGroupOptions";
import { createOldMetalIdempotencyKey } from "../utils/oldMetalPurchaseErpAdapter";
import {
  formatPoolShort,
  numPool,
  productGroupOptionDisplayName,
} from "../utils/productGroupPool";
import {
  dispatchDashboardPoolInvalidate,
  dispatchProductGroupPoolInvalidate,
} from "../utils/socketPoolEvents";

const MIN_SEARCH_LEN = 3;
const PG_DROPDOWN_VISIBLE_COUNT = 5;
const PG_DROPDOWN_ITEM_HEIGHT = 35;

function MetalTransfer() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [fromPgId, setFromPgId] = useState(null);
  const [fromPgName, setFromPgName] = useState("");
  const [fromPool, setFromPool] = useState(null);
  const [toPgId, setToPgId] = useState(null);
  const [toPgName, setToPgName] = useState("");
  const [nwt, setNwt] = useState("");
  const [pcs, setPcs] = useState("1");
  const [remarks, setRemarks] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving] = useState(false);
  const [lastDocNumber, setLastDocNumber] = useState("");

  const [pgDropdown, setPgDropdown] = useState({
    field: null,
    options: [],
    highlight: 0,
    searchCalled: false,
    loading: false,
  });

  const pgInputRefs = useRef({ from: null, to: null });
  const pgDropdownRef = useRef(null);
  const pgDropdownItemsRef = useRef([]);
  const searchTimeoutRef = useRef(null);

  const inputClassName = `form-control ${buttonClass}`;
  const inputStyle = { fontSize: "0.85rem", minHeight: "48px" };

  const mapSearchOptions = (opts) =>
    opts.map((g) => ({
      value: g.value,
      name: g.name || g.label,
      label: g.name || g.label,
      short_code: g.short_code || "",
      pool_pcs: g.pool_pcs,
      pool_nwt: g.pool_nwt,
    }));

  const searchProductGroups = useCallback(async (text) => {
    const q = String(text || "").trim();
    if (q.length < MIN_SEARCH_LEN) return [];
    const opts = await searchProductGroupSelectOptions(q, {
      branch_id: getSessionBranchId(),
    });
    return mapSearchOptions(opts);
  }, []);

  const openProductGroupDropdown = useCallback(
    (field, searchText) => {
      const q = String(searchText || "").trim();
      if (q.length < MIN_SEARCH_LEN) {
        setPgDropdown({
          field: null,
          options: [],
          highlight: 0,
          searchCalled: false,
          loading: false,
        });
        return;
      }

      setPgDropdown({
        field,
        options: [],
        highlight: 0,
        searchCalled: true,
        loading: true,
      });

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const options = await searchProductGroups(q);
          setPgDropdown({
            field,
            options,
            highlight: 0,
            searchCalled: true,
            loading: false,
          });
        } catch (e) {
          console.error(e);
          toast.error("Failed to search product groups");
          setPgDropdown({
            field: null,
            options: [],
            highlight: 0,
            searchCalled: false,
            loading: false,
          });
        }
      }, 250);
    },
    [searchProductGroups],
  );

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  const selectProductGroup = (field, option) => {
    if (field === "from") {
      setFromPgId(option?.value ?? null);
      setFromPgName(option?.name ?? option?.label ?? "");
      setFromPool({
        pool_nwt: numPool(option?.pool_nwt),
        pool_pcs: numPool(option?.pool_pcs),
      });
    } else {
      setToPgId(option?.value ?? null);
      setToPgName(option?.name ?? option?.label ?? "");
    }
    setPgDropdown({
      field: null,
      options: [],
      highlight: 0,
      searchCalled: false,
      loading: false,
    });
  };

  const handleProductGroupNameChange = (field, value) => {
    if (field === "from") {
      setFromPgName(value);
      setFromPgId(null);
      setFromPool(null);
    } else {
      setToPgName(value);
      setToPgId(null);
    }
    openProductGroupDropdown(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fromId = Number(fromPgId);
    const toId = Number(toPgId);
    const transferNwt = parseFloat(nwt);
    const transferPcs = parseInt(pcs, 10) || 0;

    if (!Number.isFinite(fromId) || fromId <= 0) {
      toast.error("Select source product group");
      return;
    }
    if (!Number.isFinite(toId) || toId <= 0) {
      toast.error("Select destination product group");
      return;
    }
    if (fromId === toId) {
      toast.error("Source and destination product groups must differ");
      return;
    }
    if (!Number.isFinite(transferNwt) || transferNwt <= 0) {
      toast.error("Enter net weight greater than 0");
      return;
    }
    if (!Number.isFinite(transferPcs) || transferPcs <= 0) {
      toast.error("Enter pieces greater than 0");
      return;
    }

    if (fromPool) {
      if (transferNwt > fromPool.pool_nwt) {
        toast.error(
          `Insufficient pool nwt (available ${fromPool.pool_nwt.toFixed(3)} g)`,
        );
        return;
      }
      if (transferPcs > fromPool.pool_pcs) {
        toast.error(
          `Insufficient pool pcs (available ${fromPool.pool_pcs})`,
        );
        return;
      }
    }

    const idempotencyKey = createOldMetalIdempotencyKey();
    const payload = {
      branch_id: getSessionBranchId(),
      idempotency_key: idempotencyKey,
      date,
      from_product_group_id: fromId,
      to_product_group_id: toId,
      nwt: transferNwt,
      pcs: transferPcs,
      remarks: remarks.trim() || undefined,
    };

    setSaving(true);
    try {
      const res = await postMetalTransfer(payload, { idempotencyKey });
      if (isErpApiSuccess(res)) {
        const docNo =
          res?.data?.doc_number ?? res?.doc_number ?? res?.data?.id ?? "";
        const fromName =
          res?.data?.from_product_group_name ?? fromPgName ?? "";
        const toName = res?.data?.to_product_group_name ?? toPgName ?? "";
        setLastDocNumber(docNo ? String(docNo) : "");
        toast.success(
          docNo
            ? `Transfer saved (${docNo})${fromName && toName ? `: ${fromName} → ${toName}` : ""}`
            : "Metal transfer saved",
        );
        setNwt("");
        setPcs("1");
        setRemarks("");
        setFromPgId(null);
        setFromPgName("");
        setFromPool(null);
        setToPgId(null);
        setToPgName("");
        dispatchProductGroupPoolInvalidate();
        dispatchDashboardPoolInvalidate();
      } else {
        toast.error(res?.message || "Metal transfer failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Metal transfer failed");
    } finally {
      setSaving(false);
    }
  };

  const renderPgDropdown = () => {
    if (!pgDropdown.field || !pgDropdown.searchCalled) return null;
    const inputEl = pgInputRefs.current[pgDropdown.field];
    if (!inputEl) return null;
    const rect = inputEl.getBoundingClientRect();
    const portal = document.getElementById("dropdown-portal") || document.body;
    const hasOptions = pgDropdown.options.length > 0;
    const visibleItems = pgDropdown.loading
      ? 1
      : hasOptions
        ? Math.min(pgDropdown.options.length, PG_DROPDOWN_VISIBLE_COUNT)
        : 1;
    const dropdownHeight = visibleItems * PG_DROPDOWN_ITEM_HEIGHT;
    const dropdownWidth = Math.max(rect.width, 200);

    pgDropdownItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        ref={pgDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${rect.bottom + window.scrollY}px`,
          left: `${rect.left + window.scrollX}px`,
          width: `${dropdownWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY:
              hasOptions &&
                pgDropdown.options.length > PG_DROPDOWN_VISIBLE_COUNT
                ? "auto"
                : "hidden",
            overflowX: "hidden",
          }}
        >
          {pgDropdown.loading ? (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
                minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
              }}
            >
              Searching…
            </div>
          ) : hasOptions ? (
            pgDropdown.options.map((option, idx) => (
              <div
                key={option.value}
                ref={(el) => {
                  pgDropdownItemsRef.current[idx] = el;
                }}
                className="dropdown-item"
                title={option.name || option.label}
                style={{
                  cursor: "pointer",
                  padding: "0.4rem 0.75rem",
                  fontSize: "0.85rem",
                  minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
                  lineHeight: "1.2",
                  backgroundColor:
                    idx === pgDropdown.highlight
                      ? "var(--theme-primary)"
                      : "transparent",
                  color: idx === pgDropdown.highlight ? "#fff" : "inherit",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                }}
                onMouseEnter={() =>
                  setPgDropdown((d) => ({ ...d, highlight: idx }))
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectProductGroup(pgDropdown.field, option);
                }}
              >
                {productGroupOptionDisplayName(option)}
              </div>
            ))
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
                minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      portal,
    );
  };

  const renderProductGroupInput = (field, label, value) => (
    <Col md={6}>
      <Form.Label className="fw-semibold" style={{ fontSize: "0.85rem" }}>
        {label}
      </Form.Label>
      <Form.Control
        ref={(el) => {
          pgInputRefs.current[field] = el;
        }}
        type="text"
        className={inputClassName}
        value={value}
        onChange={(e) => handleProductGroupNameChange(field, e.target.value)}
        onClick={() => {
          if (value && value.length >= MIN_SEARCH_LEN) {
            openProductGroupDropdown(field, value);
          }
        }}
        onKeyDown={(e) => {
          if (!pgDropdown.field || pgDropdown.field !== field) return;
          if (e.key === "ArrowDown" && pgDropdown.options.length > 0) {
            e.preventDefault();
            setPgDropdown((d) => {
              const next =
                d.highlight < d.options.length - 1 ? d.highlight + 1 : 0;
              setTimeout(() => {
                pgDropdownItemsRef.current[next]?.scrollIntoView({
                  block: "nearest",
                });
              }, 0);
              return { ...d, highlight: next };
            });
          } else if (e.key === "ArrowUp" && pgDropdown.options.length > 0) {
            e.preventDefault();
            setPgDropdown((d) => {
              const next =
                d.highlight > 0 ? d.highlight - 1 : d.options.length - 1;
              setTimeout(() => {
                pgDropdownItemsRef.current[next]?.scrollIntoView({
                  block: "nearest",
                });
              }, 0);
              return { ...d, highlight: next };
            });
          } else if (
            e.key === "Enter" &&
            pgDropdown.options[pgDropdown.highlight]
          ) {
            e.preventDefault();
            selectProductGroup(field, pgDropdown.options[pgDropdown.highlight]);
          } else if (e.key === "Escape") {
            setPgDropdown({
              field: null,
              options: [],
              highlight: 0,
              searchCalled: false,
              loading: false,
            });
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            setPgDropdown({
              field: null,
              options: [],
              highlight: 0,
              searchCalled: false,
              loading: false,
            });
          }, 150);
        }}
        style={inputStyle}
        autoComplete="off"
      />
      {field === "from" && fromPool != null && (
        <Form.Text className="text-muted" style={{ fontSize: "0.8rem" }}>
          Available: {formatPoolShort(fromPool.pool_nwt, fromPool.pool_pcs)}
        </Form.Text>
      )}
    </Col>
  );

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom">
            <h4 className="card-title mb-0">Metal Transfer</h4>
            <p className="text-muted small mb-0 mt-1">
              Move untagged metal from one product group pool to another
            </p>
          </div>

          <div className="card-body p-4">
            {lastDocNumber ? (
              <div
                className={`alert alert-success py-2 mb-4 ${buttonClass}`}
                role="alert"
              >
                Last transfer document: <strong>{lastDocNumber}</strong>
              </div>
            ) : null}

            <Form onSubmit={handleSubmit}>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className={inputClassName}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={inputStyle}
                  />
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                {renderProductGroupInput(
                  "from",
                  "From product group",
                  fromPgName,
                )}
                {renderProductGroupInput("to", "To product group", toPgName)}
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6} lg={4}>
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    N.Wt (g)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    min="0"
                    className={inputClassName}
                    value={nwt}
                    onChange={(e) => setNwt(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </Col>
                <Col md={6} lg={4}>
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Pcs
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    className={inputClassName}
                    value={pcs}
                    onChange={(e) => setPcs(e.target.value)}
                    style={inputStyle}
                    required
                  />
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col md={12}>
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Remarks
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className={buttonClass}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional notes"
                    style={{ fontSize: "0.85rem", resize: "vertical" }}
                  />
                </Col>
              </Row>

              <div className="text-end">
                <Button
                  type="submit"
                  variant="primary"
                  className={buttonClass}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Transfer metal"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
      {renderPgDropdown()}
    </div>
  );
}

export default MetalTransfer;
